import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';
import { Icon } from '../components/icons';
import { Empty, ErrorBox, Field, Loading, Modal, PageHead } from '../components/ui';
import { parseCoordinates, VenueMap } from '../components/VenueMap';
import { mapsLink } from '../format';
import { useT } from '../i18n';
import type { Venue } from '../types';

export function VenuesPage() {
  const t = useT();
  const [editing, setEditing] = useState<Venue | 'new' | null>(null);
  const [search, setSearch] = useState('');
  const venues = useQuery({ queryKey: ['venues'], queryFn: () => api.get<Venue[]>('/venues') });
  const list = (venues.data ?? []).filter((v) => !search || `${v.name} ${v.address ?? ''}`.toLowerCase().includes(search.toLowerCase()));
  return (
    <>
      <PageHead
        title={t('venues.title')}
        sub={t('venues.sub')}
        actions={
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            <Icon.plus />
            {t('venues.new')}
          </button>
        }
      />
      <div className="toolbar">
        <input className="input search" type="search" placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} aria-label={t('common.search')} />
      </div>
      <div className="card table-wrap">
        {venues.isLoading ? (
          <Loading />
        ) : list.length === 0 ? (
          <Empty title={t('venues.emptyTitle')} text={t('venues.emptyText')} />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('venues.name')}</th>
                <th>{t('venues.address')}</th>
                <th className="right">{t('venues.geofence')}</th>
                <th>{t('venues.location')}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((v) => (
                <tr key={v.id} className="clickable" onClick={() => setEditing(v)}>
                  <td><strong>{v.name}</strong></td>
                  <td className="muted">{v.address}</td>
                  <td className="right num">{v.geofenceRadiusM} m</td>
                  <td>
                    <a href={mapsLink(v.latitude, v.longitude)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                      {t('common.openMap')} ↗
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {editing && <VenueModal venue={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function VenueModal({ venue, onClose }: { venue?: Venue; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const [f, setF] = useState({
    name: venue?.name ?? '',
    address: venue?.address ?? '',
    notes: venue?.notes ?? '',
    radius: venue?.geofenceRadiusM ?? 200,
  });
  const [pos, setPos] = useState<{ lat: number; lng: number } | null>(venue ? { lat: venue.latitude, lng: venue.longitude } : null);
  const [paste, setPaste] = useState('');
  const [pasteError, setPasteError] = useState(false);
  const save = useMutation({
    mutationFn: () => {
      const body = { name: f.name, address: f.address || null, notes: f.notes || null, geofenceRadiusM: f.radius, latitude: pos!.lat, longitude: pos!.lng };
      return venue ? api.patch<Venue>(`/venues/${venue.id}`, body) : api.post<Venue>('/venues', body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['venues'] });
      onClose();
    },
  });
  const applyPaste = (text: string) => {
    setPaste(text);
    const c = parseCoordinates(text);
    setPasteError(Boolean(text) && !c);
    if (c) setPos(c);
  };
  return (
    <Modal
      wide
      title={venue ? t('venues.edit') : t('venues.new')}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" disabled={!f.name || !pos || save.isPending} onClick={() => save.mutate()}>{t('common.save')}</button>
        </>
      }
    >
      <ErrorBox error={save.error} />
      <div className="form-grid">
        <Field label={t('venues.name')} htmlFor="v-name">
          <input id="v-name" className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus />
        </Field>
        <Field label={t('venues.address')} htmlFor="v-addr">
          <input id="v-addr" className="input" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />
        </Field>
        <Field label={t('venues.pasteLocation')} hint={pasteError ? t('venues.pasteError') : t('venues.pasteHint')} full htmlFor="v-paste">
          <input id="v-paste" className="input" value={paste} onChange={(e) => applyPaste(e.target.value)} placeholder="42.6629, 21.1655" />
        </Field>
      </div>
      <div className="stack" style={{ gap: 8 }}>
        <div className="spread">
          <span className="label">{t('venues.clickMap')}</span>
          {pos && <span className="mono small muted">{pos.lat.toFixed(5)}, {pos.lng.toFixed(5)}</span>}
        </div>
        <VenueMap lat={pos?.lat ?? null} lng={pos?.lng ?? null} radius={f.radius} onPick={(lat, lng) => setPos({ lat, lng })} />
      </div>
      <Field label={`${t('venues.geofence')}: ${f.radius} m`} hint={t('venues.geofenceHint')} htmlFor="v-radius">
        <input id="v-radius" type="range" min={50} max={1000} step={25} value={f.radius} onChange={(e) => setF({ ...f, radius: Number(e.target.value) })} />
      </Field>
      <Field label={t('common.notes')} hint={t('venues.notesHint')} htmlFor="v-notes">
        <textarea id="v-notes" className="textarea" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      </Field>
    </Modal>
  );
}
